import { useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Link,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { East, Star } from "@mui/icons-material";
import { getPartnerLogo } from "../../service/logo.service";
import CompareToggle from "../compare-toggle/compare-toggle.component";
import {
  formatBRL,
  formatMedicalCoverage,
  meetsSchengen,
} from "../../utils/coverage.utils";
import {
  CoverageDialog,
  SchengenChip,
  usePlanPrice,
  useSelectInsurance,
} from "../insurance-offer/insurance-offer.component";

// Card vertical de um dos 3 planos em destaque no topo dos resultados.
// O card "featured" (Nossa escolha) ganha fundo azul-claro, borda azul,
// sombra e selo verde com estrela; os outros dois ficam neutros.
const HighlightCard = ({
  label,
  description,
  insurance,
  isEurope,
  featured,
}) => {
  const theme = useTheme();
  const selectInsurance = useSelectInsurance();
  const { total, perDay, passengers } = usePlanPrice(insurance);
  const [coverageOpen, setCoverageOpen] = useState(false);

  return (
    <Card
      elevation={0}
      sx={{
        position: "relative",
        overflow: "visible",
        border: "2px solid",
        borderColor: featured ? "primary.main" : "grey.100",
        backgroundColor: featured
          ? alpha(theme.palette.primary.main, 0.04)
          : "background.paper",
        boxShadow: featured
          ? `0 10px 30px -12px ${alpha(theme.palette.primary.main, 0.45)}`
          : "none",
        transform: { md: featured ? "translateY(-6px)" : "none" },
        borderRadius: theme.spacingValues.medium,
        p: 2.5,
        pt: 3,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      }}
    >
      <Chip
        label={label}
        icon={featured ? <Star /> : undefined}
        color={featured ? "success" : "default"}
        variant={featured ? "filled" : "outlined"}
        sx={{
          position: "absolute",
          top: -14,
          left: 16,
          fontWeight: 700,
          backgroundColor: featured ? undefined : "background.paper",
          ...(featured && {
            boxShadow: `0 4px 12px -4px ${alpha(theme.palette.success.main, 0.6)}`,
          }),
        }}
      />
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {description}
      </Typography>
      <Box
        sx={{
          height: 40,
          "& svg": { height: "100%", width: "auto", maxWidth: "100%" },
        }}
      >
        {getPartnerLogo(insurance.insuranceCompany)?.component.dark}
      </Box>
      <Typography sx={{ fontWeight: 700, color: "primary.main" }}>
        {insurance.insuranceName}
      </Typography>
      <Box>
        <Typography sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
          Despesas médicas
        </Typography>
        <Typography
          sx={{ fontWeight: 800, fontSize: "1.35rem", lineHeight: 1.2 }}
        >
          {formatMedicalCoverage(insurance.healthCoverage, isEurope)}
        </Typography>
      </Box>
      {isEurope && meetsSchengen(insurance.healthCoverage) && <SchengenChip />}
      {insurance.coverageList?.length > 0 && (
        <Link
          component="button"
          variant="body2"
          onClick={() => setCoverageOpen(true)}
          sx={{ width: "fit-content", textAlign: "left" }}
        >
          Ver cobertura completa
        </Link>
      )}
      <Box sx={{ mt: "auto", pt: 1 }}>
        <Typography
          sx={{ fontWeight: 800, fontSize: "1.5rem", lineHeight: 1.1 }}
        >
          {formatBRL(total)}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {passengers > 1 ? `total para ${passengers} pessoas` : "total"}
          {perDay > 0 && ` · ${formatBRL(perDay)}/dia`}
        </Typography>
      </Box>
      <Button
        variant="contained"
        onClick={() => selectInsurance(insurance)}
        endIcon={<East sx={{ transform: "rotate(-45deg)" }} />}
      >
        Selecionar
      </Button>
      <Box sx={{ alignSelf: "center", mt: -0.5 }}>
        <CompareToggle insurance={insurance} />
      </Box>
      <CoverageDialog
        insurance={insurance}
        open={coverageOpen}
        onClose={() => setCoverageOpen(false)}
      />
    </Card>
  );
};

// Faixa com os 3 destaques: Melhor preço / Recomendado / Mais cobertura.
// `highlights` vem pronto do FilteredResultList: [{ label, description, insurance }].
const PlanHighlights = ({ highlights, isEurope }) => {
  if (!highlights.length) return null;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: `repeat(${highlights.length}, 1fr)`,
        },
        gap: { xs: 3, md: 2.5 },
        mt: 3,
      }}
    >
      {highlights.map((highlight) => (
        <HighlightCard
          key={highlight.label}
          {...highlight}
          isEurope={isEurope}
        />
      ))}
    </Box>
  );
};

export default PlanHighlights;
